<template>
  <div class="cart-item" :class="{ 'cart-item--disabled': isOddDisabled }">
    <div class="cart-item__left">
      <button v-if="!readonly" class="cart-item__remove" @click="removeOdd">
        <IllinoisIcons icon="close" height="20" width="20"/>
      </button>
      <div class="cart-item__info">
        <div class="cart-item__outcome">
          {{ outcomeName }}
          {{ specifier ? formatLineDisplay(specifier, marketId)  : ''}}
        </div>
        <div class="cart-item__market">
          {{ marketName }}
          <span v-if="earlyPayout && earlyPayoutMargin" class="match-badges">
            <FeatureBadge badge-key="earlyPayout" />
          </span>
        </div>
        <div class="cart-item__event">{{ eventName }}</div>
      </div>
    </div>

    <div class="cart-item__right">
      <div class="cart-item__odd-wrap" :class="{ 'cart-item__odd-wrap--updated': isOddUpdated }">
        <span class="cart-item__odd">{{ oddValue | odds_type_filter }}</span>
        <OddBoostBadge :odd="boostDescriptor" size="sm"/>
        <button
          v-if="showFixedBase"
          class="cart-item__base-btn"
          :class="{ 'cart-item__base-btn--active': isFixedBase }"
          @click="toggleFixedBase"
        >B</button>
      </div>
    </div>
  </div>
</template>

<script>
import IllinoisIcons from "@/components/american/icons.vue";
import OddBoostBadge from "@/components/generic/OddBoostBadge.vue";
import FeatureBadge from "@/components/badges/FeatureBadge.vue";
import { mapGetters } from 'vuex';
import {MARKET_ASIAN_HANDICAP} from "@/constants/marketConstants";

export default {
  name: 'CartItemAmerican',
  components: {IllinoisIcons, OddBoostBadge, FeatureBadge},
  props: {
    outcomeName: { type: String, required: true },
    marketName:  { type: String, required: true },
    marketId:  { type: String, required: true },
    eventName:   { type: String, required: true },
    oddValue:    { type: String, required: true },
    uniqueId:    { type: String, required: true },
    specifier:   { type: String, default: '' },
    isCartOpen:  { type: Boolean, default: false },
    betType:     { type: String, default: '' },
    readonly:    { type: Boolean, default: false },
    // Optional — the parent (CartAmerican) reads these from the cart's
    // odd payload and passes them through. When undefined the badge stays
    // hidden. Kept as separate props (not `odd: Object`) to preserve the
    // simple shape of this leaf component.
    boostApplied: { type: Boolean, default: false },
    boostPercent: { type: Number, default: null },
    // Same shape and the same reason as the boost pair above.
    // `cartHandler.refactorOddV3` decides this per leg, matching the bet's
    // (marketId, outcome_name) against `event.earlyPayoutOutcomes`, so by the
    // time an odd reaches the cart the answer is already on it — the classic
    // slip reads `odd.earlyPayout` directly (BetOddsSummaryMarketViewV3), this
    // one gets it passed because it is a leaf that takes scalars.
    earlyPayout:       { type: Boolean, default: false },
    earlyPayoutMargin: { type: Number,  default: null },
  },
  computed: {
    ...mapGetters({
      'getMarketConfig': 'settings/getMarketConfig',
    }),
    // Reshape the two boost props into the descriptor shape OddBoostBadge
    // expects. Keeps the badge contract centralized (`{boostApplied,
    // boostPercent}`) without leaking that detail across every parent.
    boostDescriptor() {
      return {boostApplied: this.boostApplied === true, boostPercent: this.boostPercent};
    },
    getBet() {
      return this.$store.getters['cart/getBet'];
    },
    isFixedBase() {
      return this.getBet?._system_detail?.fixed_bases?.includes(this.uniqueId) || false;
    },
    showFixedBase() {
      return this.isCartOpen && this.betType === 'system';
    },
    changedOdds() {
      return this.getBet?.odds?.filter(o => o.changed) || [];
    },
    isOddUpdated() {
      return this.getBet?.odds?.some(o => o.unique_id === this.uniqueId && o.changed) || false;
    },
    isOddDisabled() {
      return this.getBet?.odds?.some(o => o.unique_id === this.uniqueId && (o.to_remove || o.suspended)) || false;
    },
  },
  methods: {
    removeParens(str) {
      return str ? str.replace(/[()]/g, '') : str;
    },

    stripScoreState(str, market) {
      if (!str) return str;

      let st = String(str).replace(/\s*\(\d+-\d+\)\s*$/, '').trim();

      if (MARKET_ASIAN_HANDICAP.includes(market) && !st.startsWith('-'))
        return '+' + st;

      return st
    },
    formatLineDisplay(line, market) {
      if (!line) return '';
      let str = this.stripScoreState(String(line).trim());

      if (this.isHandicapMarket(market)) {
        if (/^[0-9]/.test(str) && parseFloat(str) !== 0) {
          return '+' + str;
        }
      }

      if (MARKET_ASIAN_HANDICAP.includes(market) && !str.startsWith('-'))
        return '+' + str;

      return str;
    },
    isHandicapMarket(market) {
      if (!market) return false;
      const id = parseInt( market, 10);
      return this.getMarketConfig.handicapIds.includes(id);
    },
    removeOdd() {
      this.$store.dispatch('cart/removeOddFromCart', { unique_id: this.uniqueId });
    },
    async toggleFixedBase() {
      await this.$store.dispatch('cart/checkFixedBaseSystem', { unique_id: this.uniqueId });
    },
  },
};
</script>

<style scoped>
.cart-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  transition: opacity 0.2s;
}
.cart-item:last-child {
  border-bottom: none;
}
.cart-item--disabled {
  opacity: 0.4;
}
.cart-item__left {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.cart-item__remove {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: var(--text-cart);
  flex-shrink: 0;
  transition: color 0.15s;
  display: flex;
  align-items: center;
  margin-top: 2px;
}
.cart-item__remove:hover {
  color: var(--danger, #ef4444);
}
.cart-item__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.cart-item__outcome {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--color-accent, var(--color-primary));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cart-item__specifier {
  font-size: var(--font-size-xl);
  font-weight: 400;
  color: var(--text-cart);
}
.cart-item__market {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: var(--text-cart);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cart-item__event {
  font-size: var(--font-size-xl);
  color: var(--text-cart);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cart-item__right {
  flex-shrink: 0;
  padding-left: 8px;
}
.cart-item__odd-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  border-radius: 4px;
  padding: 2px 4px;
}
.cart-item__odd-wrap--updated {
  background: rgba(245, 158, 11, 0.15);
}
.cart-item__odd {
  font-size: var(--font-size-4xl);
  font-weight: 700;
  color: var(--text-cart);
}
.cart-item__base-btn {
  font-size: var(--font-size-xl);
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background: var(--bg-card);
  color: var(--text-op-50-platform);
  transition: all 0.15s;
  min-width: 24px;
}
.cart-item__base-btn--active {
  background: var(--color-primary);
  color: var(--btn-text, #fff);
}
</style>
